/**
 * MinutesStudio — the converged studio the bake-off selected: a document-first
 * canvas with the copilot chat as a FLOATING overlay dock, the details form as
 * a second (mutually exclusive) overlay, and the server DraftSession as the
 * default sync layer.
 *
 * Layout contract:
 *   - The document owns the full width, always. Overlays float over its right
 *     edge and are RESIZABLE by dragging their left edge (same interaction as
 *     the AI chat's artifact panel); widths persist per-overlay.
 *   - No floating buttons over the document — the overlays open and close from
 *     the top bar only ("Ask Vee" / "Details").
 *   - Desktop: the document re-centers beside an open overlay (animated
 *     padding). Narrow (<1100px): overlays become full-height edge sheets and
 *     the padding shift is disabled.
 *
 * Sync (all default, nothing to press):
 *   dock draft   → applied to the canvas instantly + session refresh for rev.
 *   chat on /agent → DraftSession poll/focus-refetch picks it up here.
 *   hand-edits   → PATCH on click-away; they become the chat's next baseline.
 *   update_page_config → form patched live via useMinutesCopilotBridge.
 *   conflicts    → rev mismatch shows the take-theirs / keep-mine banner.
 */
import { useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import {
  FiMessageSquare, FiZap, FiRefreshCw, FiAlertTriangle, FiSliders, FiX,
} from 'react-icons/fi';
import Button from '../common/Button';
import ExportMenu from '../common/ExportMenu';
import Icon from '../common/Icon';
import PageContinuityBanner from '../common/PageContinuityBanner';
import StartPageOverride from '../common/StartPageOverride';
import { useApp } from '../../context/AppContext';
import { useUI } from '../../context/UIContext';
import { useMeetingConfig } from '../../context/MeetingConfigContext';
import { useDraftSession } from '../../hooks/useDraftSession';
import { useDocumentExport } from '../../hooks/useDocumentExport';
import { aiAPI, draftsAPI, minutesLedgerAPI } from '../../services/api';
import { htmlToPlainText } from '../../utils/draftUtils';
import { sanitizeDocHtml } from '../../utils/sanitizeHtml';
import { getMeetingMinutesPreview } from '../../utils/documentPreviews';
import { DOC_CSS_SCOPED } from '../../utils/docCss';
import { buildDocBasename } from '../../utils/downloadFilename';
import CopilotDock from '../../chat/CopilotDock';
import StudioConfigSections from './StudioConfigSections';
import { useMinutesCopilotBridge } from './useMinutesCopilotBridge';

// docSource → chip spec (line icon + label — app-wide no-emoji rule).
const SOURCE_CHIP = {
  ai: { icon: 'ai', label: 'AI draft', color: '#145f5f', bg: 'rgba(20,95,95,0.08)' },
  'targeted-edit': { icon: 'ai', label: 'Edited by Vee', color: '#145f5f', bg: 'rgba(20,95,95,0.08)' },
  fallback: { icon: 'warning', label: 'Standard template', color: '#8a6a1f', bg: 'rgba(176,137,58,0.12)' },
  'user-edited': { icon: 'edit', label: 'Edited by you', color: '#5a5145', bg: '#f0ebe0' },
};

const OVERLAY_SHADOW = 'var(--sh-3, 0 20px 60px rgba(8,17,31,0.18))';
const MIN_OVERLAY_W = 300;
const MAX_OVERLAY_W = 680;
const readWidth = (key, fallback) => {
  try {
    const n = Number(localStorage.getItem(key));
    return Number.isFinite(n) && n >= MIN_OVERLAY_W && n <= MAX_OVERLAY_W ? n : fallback;
  } catch { return fallback; }
};

export default function MinutesStudio() {
  const { activeCompany, activeCompanyCin, companies, selectCompany } = useApp();
  const { showToast } = useUI();
  const {
    config, agendaChecked, customItems, directorsChecked, customDirectors,
    absentDirectors, invitees, restoreConfig, buildPayload, companyContext,
  } = useMeetingConfig();

  // activeCompanyCin (synchronous, from localStorage), NOT activeCompany?.cin
  // (null until the companies API resolves): keying the draft session on the
  // latter left companyCin undefined for the whole companies-fetch round-trip
  // on a cold load, so the studio showed its empty state — "no draft" — before
  // it had even asked. The cached document keys off this CIN and paints in the
  // first render instead.
  const { session, hydrated, conflict, save, refresh, takeTheirs, keepMine } = useDraftSession(activeCompanyCin || activeCompany?.cin, 'minutes');

  const bodyRef = useRef(null);
  const editingRef = useRef(false);
  const editedRef = useRef(false); // any hand-edit since the last AI version
  const lastRenderedRev = useRef(0);
  const [generating, setGenerating] = useState(false);
  const [dirty, setDirty] = useState(false);
  // One overlay at a time over the document's right edge.
  const [overlay, setOverlay] = useState('dock'); // 'dock' | 'inspector' | null

  // Origin-aware routing: the AI Agent history reopens minutes-born chats
  // HERE — make sure the dock is up so the conversation is visible on arrival.
  const location = useLocation();
  const openChatSessionId = location.state?.openChatSessionId || null;
  useEffect(() => {
    if (openChatSessionId) setOverlay('dock');
  }, [openChatSessionId]);

  // "Ask Vee about this": select any passage in the document → floating
  // affordance → the passage becomes a quote chip on the dock's composer
  // (same interaction as the AI chat's artifact panel).
  const [selection, setSelection] = useState(null); // { text, x, y }
  const [quotedSelection, setQuotedSelection] = useState(null);
  const handleCanvasMouseUp = () => {
    const sel = window.getSelection();
    const text = sel ? String(sel.toString()).trim() : '';
    if (!text || !bodyRef.current || !sel.rangeCount || !bodyRef.current.contains(sel.anchorNode)) {
      setSelection(null);
      return;
    }
    const rect = sel.getRangeAt(0).getBoundingClientRect();
    setSelection({ text: text.slice(0, 1200), x: rect.left + rect.width / 2, y: rect.top });
  };
  const quoteIntoDock = () => {
    if (!selection) return;
    setQuotedSelection(selection.text);
    setSelection(null);
    window.getSelection()?.removeAllRanges();
    setOverlay('dock');
  };

  // ── Resizable overlays (artifact-panel interaction): drag the left edge; ──
  // widths persist per overlay so the studio reopens the way you left it.
  const [dockW, setDockW] = useState(() => readWidth('cs_minutes_dock_w', 380));
  const [inspW, setInspW] = useState(() => readWidth('cs_minutes_insp_w', 330));
  const resizing = useRef(null); // { which, startX, baseW }
  const startResize = (which) => (e) => {
    e.preventDefault();
    resizing.current = { which, startX: e.clientX, baseW: which === 'dock' ? dockW : inspW };
    document.body.style.userSelect = 'none';
    document.body.style.cursor = 'col-resize';
    const move = (ev) => {
      const r = resizing.current;
      if (!r) return;
      const w = Math.min(MAX_OVERLAY_W, Math.max(MIN_OVERLAY_W, r.baseW + (r.startX - ev.clientX)));
      if (r.which === 'dock') setDockW(w); else setInspW(w);
    };
    const up = () => {
      const r = resizing.current;
      resizing.current = null;
      document.body.style.userSelect = '';
      document.body.style.cursor = '';
      window.removeEventListener('mousemove', move);
      window.removeEventListener('mouseup', up);
      try {
        if (r) {
          // Read the latest width from state on the next tick via the setter form.
          if (r.which === 'dock') setDockW((w) => { localStorage.setItem('cs_minutes_dock_w', String(w)); return w; });
          else setInspW((w) => { localStorage.setItem('cs_minutes_insp_w', String(w)); return w; });
        }
      } catch { /* ignore */ }
    };
    window.addEventListener('mousemove', move);
    window.addEventListener('mouseup', up);
  };

  // Narrow screens (< 1100px): overlays become full-height edge sheets and the
  // document padding shift is disabled (no spare width to re-center into).
  const [narrow, setNarrow] = useState(() => window.matchMedia('(max-width: 1100px)').matches);
  useEffect(() => {
    const mq = window.matchMedia('(max-width: 1100px)');
    const onChange = (e) => setNarrow(e.matches);
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, []);

  const overlayWidth = overlay === 'dock' ? dockW : inspW;
  // top-4/right-4 keeps clear air between the overlay and the top bar so the
  // dock never reads as clamped against it.
  const overlayFrame = narrow
    ? { className: 'top-0 right-0 bottom-0 max-w-[94%]', style: { width: 420, borderRadius: 'var(--r-xl, 16px) 0 0 var(--r-xl, 16px)' } }
    : { className: 'top-4 right-4 bottom-4 max-w-[92%]', style: { width: overlayWidth, borderRadius: 'var(--r-xl, 16px)' } };

  // Defensive normalization — older sessions may carry raw pipeline sources.
  const rawSource = session?.docSource || 'preview';
  const docSource = dirty || editedRef.current ? 'user-edited'
    : SOURCE_CHIP[rawSource] || rawSource === 'preview' ? rawSource
      : /fallback/i.test(rawSource) ? 'fallback' : 'ai';

  const bridge = useMinutesCopilotBridge({
    // Both versions ride to the dock, not just the state label: the live canvas
    // (read from the DOM, so edits count the moment they are typed — no waiting
    // for the click-away commit) and Vee's last version before those edits. The
    // difference between them is the user's work, and it is the only way Vee can
    // honour "apply this but keep my edits". Defined-later closures are fine —
    // this only runs when a message is actually sent.
    getDocState: () => ({
      docSource,
      hasUserEdits: editedRef.current,
      currentHtml: currentHtml(),
      baselineHtml: lastAiSnapshotRef.current?.html || '',
    }),
    isInspectorOpen: () => overlay === 'inspector',
  });

  // ── Numbered-export page continuity (same ledger as the Classic view) ────
  const [pdfBusy, setPdfBusy] = useState(false);
  const [docxBusy, setDocxBusy] = useState(false);
  const [ledgerInfo, setLedgerInfo] = useState({ suggestedStartPage: 1, hasHistory: false, eligible: false });
  const [startPageOverride, setStartPageOverride] = useState(null);
  const [ledgerRefreshTick, setLedgerRefreshTick] = useState(0);

  const meetingNumber = config.meetingNo === 'custom' ? (config.meetingNoCustom || '') : (config.meetingNo || '');
  const fileBase = buildDocBasename({
    company: companyContext?.name || activeCompany?.name || 'Company',
    docType: `${config.meetingType || 'board'} minutes`,
    identifier: meetingNumber,
    date: config.date,
  });

  // Plain exports (copy / .txt / .html / print-to-PDF) — the same hook every
  // document surface uses, pointed at the live canvas.
  const {
    copy: copyDoc, downloadTxt, downloadHtml, print: printDoc, getHtml: getExportHtml,
  } = useDocumentExport(bodyRef, {
    title: `Minutes - ${companyContext?.name || activeCompany?.name || 'Company'}`,
    filename: fileBase,
    fallbackHtml: session?.docHtml || '',
    onNotify: showToast,
  });

  const configSnapshot = () => ({
    config, agendaChecked, customItems, directorsChecked, customDirectors, absentDirectors, invitees,
  });

  // Accidental-edit guards: the committed baseline (post-browser-parse, same
  // normalization as currentHtml) and Vee's last non-edited version so a
  // mistaken edit can be discarded entirely.
  const lastCommittedHtmlRef = useRef('');
  const lastAiSnapshotRef = useRef(null); // { html, plainText, docSource }

  const renderDoc = (html) => {
    if (!bodyRef.current) return;
    bodyRef.current.innerHTML = sanitizeDocHtml(html || '');
    // Snapshot AFTER assignment — the browser normalizes markup on parse, so
    // this is byte-comparable with currentHtml() at blur time.
    lastCommittedHtmlRef.current = sanitizeDocHtml(bodyRef.current.innerHTML);
  };

  // Server → canvas (chat on /agent, other devices, background refresh).
  useEffect(() => {
    if (!session || !bodyRef.current) return;
    if (session.rev === lastRenderedRev.current) return;
    if (editingRef.current) return; // never clobber the caret
    // A restore is mid-flight: its document wins, so don't paint the session's
    // (possibly other company's) version over it in the gap before it saves.
    if (pendingRestoreRef.current || restoringRef.current) return;
    renderDoc(session.docHtml);
    const fromChat = session.lastWriter === 'chat';
    if (fromChat && lastRenderedRev.current > 0) showToast('Document updated from the AI chat', 'info');
    if (fromChat && session.config) restoreConfig(session.config);
    // Any non-user-edited version becomes the "discard my edits" restore point.
    if (session.docSource !== 'user-edited') {
      lastAiSnapshotRef.current = { html: session.docHtml, plainText: session.docPlainText || '', docSource: session.docSource };
    }
    lastRenderedRev.current = session.rev;
    editedRef.current = false;
    setDirty(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session]);

  // ── Arrivals that carry a document with them ────────────────────────────
  // Two routes land here with work already done elsewhere:
  //   My Documents → { draftId }      (a saved minutes draft)
  //   AI Agent     → { chatHandoff }  ("Open in Minutes studio" on a chat draft)
  // Both restore the form + document AND write them into the shared
  // DraftSession, so the chat's next revision baseline is what's on screen.
  const restoredRef = useRef(false);
  // A restore cannot simply paint on arrival: the canvas only mounts once
  // there is content (so bodyRef may still be null), and a draft belonging to
  // another company needs the workspace switched first — which swaps the
  // DraftSession this component writes to. So the payload is PARKED here and
  // applied by the effect below once both conditions hold.
  const pendingRestoreRef = useRef(null); // { html, config, source, cin, note }
  const restoringRef = useRef(false);
  const [restorePending, setRestorePending] = useState(false);

  const queueRestore = (payload) => {
    pendingRestoreRef.current = payload;
    setRestorePending(true); // also forces the canvas to mount (see hasContent)
  };

  const adoptDocument = async ({ html, config: cfg, source }) => {
    restoringRef.current = true;
    if (cfg) restoreConfig(cfg);
    renderDoc(html || '');
    editedRef.current = false;
    setDirty(false);
    const docSrc = /fallback/i.test(String(source || '')) ? 'fallback'
      : source === 'user-edited' ? 'user-edited' : 'ai';
    lastAiSnapshotRef.current = docSrc === 'user-edited'
      ? lastAiSnapshotRef.current
      : { html: html || '', plainText: htmlToPlainText(html || ''), docSource: docSrc };
    const res = await save({
      ...(cfg ? { config: cfg } : {}),
      docHtml: html || '',
      docPlainText: htmlToPlainText(html || ''),
      docSource: docSrc,
    }).catch(() => null);
    if (res?.session) lastRenderedRev.current = res.session.rev;
    restoringRef.current = false;
  };

  // Apply a parked restore once the canvas exists AND — when the draft names a
  // company — the workspace has actually switched to it, so the write lands in
  // that company's session rather than the one we arrived on.
  useEffect(() => {
    const p = pendingRestoreRef.current;
    if (!p || !bodyRef.current) return;
    const activeCin = String(activeCompany?.cin || '').toUpperCase();
    if (p.cin && activeCin && p.cin !== activeCin) return; // still switching
    pendingRestoreRef.current = null;
    setRestorePending(false);
    adoptDocument(p).then(() => { if (p.note) showToast(p.note, 'success'); });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [restorePending, activeCompany, session]);

  useEffect(() => {
    const handoff = location.state?.chatHandoff;
    if (!handoff || restoredRef.current) return;
    restoredRef.current = true;
    queueRestore({
      html: handoff.draftHtml,
      config: handoff.meetingConfig,
      source: handoff.source,
      note: 'Draft opened from AI chat — Vee is right here to revise it',
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.state?.chatHandoff]);

  useEffect(() => {
    const draftId = location.state?.draftId;
    if (!draftId || restoredRef.current) return;
    restoredRef.current = true;
    draftsAPI.getById(draftId).then((res) => {
      const draft = res?.data;
      if (!draft) { showToast('Could not open that saved draft', 'error'); return; }
      // A saved draft may belong to another company — the studio is keyed to
      // the ACTIVE company's session, so switch the workspace first and let
      // the apply-effect wait for the switch before writing.
      const cin = String(draft.companyCin || '').toUpperCase();
      let awaitCin = null;
      if (cin && cin !== String(activeCompany?.cin || '').toUpperCase()) {
        const target = (companies || []).find((c) => String(c.cin || '').toUpperCase() === cin);
        if (target) {
          selectCompany(target);
          // Only wait when a switch was actually started. If the company isn't
          // in the user's list (shared/removed), applying immediately shows the
          // document instead of parking on it forever.
          awaitCin = cin;
        }
      }
      queueRestore({
        html: draft.docHtml,
        config: draft.minutesConfig,
        source: draft.docSource,
        cin: awaitCin,
        note: 'Restored saved meeting minutes draft',
      });
    }).catch(() => {
      // Never fail silently on a user-initiated open — they clicked a document
      // and must not be left staring at an unrelated one.
      showToast('Could not open that saved draft', 'error');
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.state?.draftId]);

  const currentHtml = () => sanitizeDocHtml(bodyRef.current?.innerHTML || getExportHtml?.() || '');

  // Hand-edits commit on click-away — they become the chat's next baseline.
  // MISTAKE-PROOFING: only a REAL content change commits. A stray click, a
  // keystroke that was undone (Ctrl+Z back to the original), or a caret-only
  // interaction compares equal to the committed baseline → nothing syncs,
  // nothing gets marked "Edited by you".
  const commitEdits = async () => {
    editingRef.current = false;
    if (!dirty) return;
    const html = currentHtml();
    if (html === lastCommittedHtmlRef.current) {
      setDirty(false); // no actual change — false alarm
      return;
    }
    editedRef.current = true;
    // Based on the version actually on screen — see save()'s baseRev note. If a
    // newer version landed while they were typing, this 409s into the conflict
    // card instead of quietly discarding that version.
    const res = await save(
      { docHtml: html, docPlainText: htmlToPlainText(html), docSource: 'user-edited' },
      { baseRev: lastRenderedRev.current || undefined },
    ).catch(() => null);
    if (res?.session) {
      lastRenderedRev.current = res.session.rev;
      lastCommittedHtmlRef.current = html;
      setDirty(false);
    }
  };

  // Escape hatch for edits that DID slip through: restore Vee's last version
  // (kept in memory for this visit) and sync it back as the working draft.
  const discardMyEdits = async () => {
    const snap = lastAiSnapshotRef.current;
    if (!snap) return;
    renderDoc(snap.html);
    editedRef.current = false;
    setDirty(false);
    const res = await save({ docHtml: snap.html, docPlainText: snap.plainText, docSource: snap.docSource }).catch(() => null);
    if (res?.session) lastRenderedRev.current = res.session.rev;
    showToast('Your edits were discarded — back to Vee’s version', 'success');
  };

  // Dock draft → canvas instantly; the session refresh trues up the rev.
  // A CLIENT-EDITED draft (the dock's placeholder fill) is persisted from
  // here — the server never saw that change, so refresh alone can't carry it.
  const handleDockArtifact = async (draft) => {
    if (!draft || draft.kind !== 'minutes') {
      if (draft) showToast('Open non-minutes drafts from the Vee page', 'info');
      return;
    }
    renderDoc(draft.html);
    editedRef.current = Boolean(draft.edited);
    setDirty(false);
    if (!draft.edited) {
      lastAiSnapshotRef.current = {
        html: draft.html || '',
        plainText: draft.plainText || '',
        docSource: /fallback/i.test(String(draft.source || '')) ? 'fallback' : 'ai',
      };
    }
    if (draft.meetingConfig) restoreConfig(draft.meetingConfig);
    if (draft.edited) {
      const res = await save({
        docHtml: draft.html || '',
        docPlainText: draft.plainText || '',
        docSource: 'user-edited',
      }).catch(() => null);
      if (res?.session) lastRenderedRev.current = res.session.rev;
    } else {
      const s = await refresh();
      if (s) lastRenderedRev.current = s.rev;
    }
  };

  const handleGenerate = async () => {
    setGenerating(true);
    try {
      const res = await aiAPI.draftMeetingMinutes(buildPayload(), companyContext, { useCache: false });
      const data = res?.data || {};
      if (!res?.success || !data.html) throw new Error(res?.message || 'Generation failed');
      const out = await save({
        config: configSnapshot(),
        docHtml: data.html,
        docPlainText: htmlToPlainText(data.html),
        docSource: /fallback/i.test(String(data.source || '')) ? 'fallback' : 'ai',
      });
      if (out?.session) {
        renderDoc(out.session.docHtml);
        lastRenderedRev.current = out.session.rev;
        editedRef.current = false;
        setDirty(false);
        lastAiSnapshotRef.current = {
          html: out.session.docHtml,
          plainText: out.session.docPlainText || '',
          docSource: out.session.docSource,
        };
        showToast('Minutes generated', 'success');
      } else if (out?.conflict) {
        showToast('Vee updated this draft at the same time — resolve the conflict below', 'warning');
      }
    } catch (e) {
      showToast(e.message || 'Could not generate minutes', 'error');
    } finally {
      setGenerating(false);
    }
  };

  const handleNumberedPdf = async () => {
    if (!activeCompany) { showToast('Select a company first', 'warning'); return; }
    const bodyHtml = currentHtml();
    if (!bodyHtml.trim()) { showToast('Generate the minutes first', 'warning'); return; }
    setPdfBusy(true);
    try {
      const res = await minutesLedgerAPI.downloadNumberedPdf({
        title: `Minutes - ${companyContext?.name || activeCompany?.name || 'Company'}`,
        bodyHtml,
        companyCin: companyContext?.cin || activeCompany?.cin || '',
        meetingType: config.meetingType,
        meetingDate: config.date, // ISO — the server derives the FY from this
        meetingNumber,
        startPageOverride: Number.isInteger(startPageOverride) ? startPageOverride : undefined,
        filename: fileBase,
      });
      const range = res.startPage && res.endPage ? ` — pages ${res.startPage}–${res.endPage}` : '';
      showToast(`Numbered PDF downloaded${range}${res.reissued ? ' (re-issued)' : ''}`, 'success');
      setStartPageOverride(null);
      setLedgerRefreshTick((t) => t + 1);
    } catch (e) {
      showToast(e.message || 'Numbered PDF export failed', 'error');
    } finally {
      setPdfBusy(false);
    }
  };

  const handleNumberedDocx = async () => {
    if (!activeCompany) { showToast('Select a company first', 'warning'); return; }
    const bodyHtml = currentHtml();
    if (!bodyHtml.trim()) { showToast('Generate the minutes first', 'warning'); return; }
    setDocxBusy(true);
    try {
      const res = await minutesLedgerAPI.downloadNumberedDocx({
        title: `Minutes - ${companyContext?.name || activeCompany?.name || 'Company'}`,
        bodyHtml,
        meeting: buildPayload(),
        companyContext,
        companyCin: companyContext?.cin || activeCompany?.cin || '',
        meetingType: config.meetingType,
        meetingDate: config.date,
        meetingNumber,
        startPageOverride: Number.isInteger(startPageOverride) ? startPageOverride : undefined,
        filename: fileBase,
      });
      const range = res.startPage && res.endPage ? ` — pages ${res.startPage}–${res.endPage}` : '';
      showToast(`Numbered DOCX downloaded${range}${res.reissued ? ' (re-issued)' : ''}`, 'success');
      setStartPageOverride(null);
      setLedgerRefreshTick((t) => t + 1);
    } catch (e) {
      showToast(e.message || 'Numbered DOCX export failed', 'error');
    } finally {
      setDocxBusy(false);
    }
  };

  // ── "Let Vee fill the gaps" ──────────────────────────────────────────────
  // Only fields a drafter would actually expect Vee to source; the ask names
  // them explicitly so the reply is a targeted fill, not a re-interview.
  const GAP_FIELDS = [
    ['date', 'date of the meeting'],
    ['startTime', 'start time'],
    ['endTime', 'end time'],
    ['venue', 'venue'],
    ['noticeDate', 'notice date'],
    ['chairperson', 'chairperson'],
    ['companySecretary', 'company secretary'],
    ['statutoryAuditor', 'statutory auditor'],
  ];
  const missingFields = GAP_FIELDS
    .filter(([k]) => !String(config[k] || '').trim())
    .map(([, label]) => label)
    .concat(config.meetingNo || config.meetingNoCustom ? [] : ['meeting number']);

  const [autoSend, setAutoSend] = useState(null);

  // Conflict banner's smart third way: keep MY version as the working draft,
  // then have Vee re-apply ITS update on top with targeted edits — mine wins
  // wherever the two touch the same text, and Vee reports what it skipped.
  const letVeeMerge = async () => {
    const html = currentHtml();
    const res = await keepMine({ docHtml: html, docPlainText: htmlToPlainText(html), docSource: 'user-edited' });
    if (!res?.session) return; // still conflicted — the banner stays up
    setOverlay('dock');
    setAutoSend({
      text: 'You updated this document while I was hand-editing it, so there were two versions. '
        + 'I have kept MY edited version as the working draft. Using targeted edits, re-apply what your update '
        + 'changed on top of my version — but never overwrite any of my manual edits. Where the two conflict, '
        + 'keep mine and tell me exactly what you skipped.',
    });
  };

  const askVeeToFillGaps = () => {
    setOverlay('dock');
    setAutoSend({
      text: `Fill in what you can on the meeting form: ${missingFields.join(', ')}.\n\n`
        + 'Use the company record where it is authoritative (chairperson, statutory auditor, registered office as venue) '
        + 'and anything I have already told you in this chat. For anything you cannot source that way, ask me instead of guessing — '
        + 'leave the field blank until I answer.',
    });
  };

  const hasDoc = Boolean(session && String(session.docHtml || '').trim());
  // `restorePending` keeps the canvas mounted while an incoming draft is being
  // applied — otherwise the empty state renders, bodyRef is null, and the
  // restore paints into nothing.
  const hasContent = hasDoc || dirty || restorePending;
  const chip = SOURCE_CHIP[docSource] || null;
  const exportBusy = pdfBusy || docxBusy;

  const exportItems = [
    { key: 'copy', label: 'Copy text', onClick: copyDoc, disabled: !hasContent },
    { key: 'txt', label: 'Plain text (.txt)', onClick: downloadTxt, disabled: !hasContent },
    { key: 'html', label: 'Web page (.html)', onClick: downloadHtml, disabled: !hasContent },
    { key: 'print', label: 'Print / PDF', hint: 'Browser print dialog — save as a plain PDF', onClick: printDoc, disabled: !hasContent },
    {
      key: 'pdf',
      label: pdfBusy ? 'Numbering PDF…' : 'Numbered PDF',
      hint: 'Continuous statutory page numbers (FY ledger)',
      onClick: handleNumberedPdf,
      disabled: exportBusy || !hasContent,
      dividerBefore: true,
    },
    {
      key: 'docx',
      label: docxBusy ? 'Numbering DOCX…' : 'Numbered DOCX',
      hint: 'Editable, same page ledger as the PDF',
      onClick: handleNumberedDocx,
      disabled: exportBusy || !hasContent,
    },
  ];

  return (
    // One rounded surface for the whole studio — the top bar and canvas share
    // a single card silhouette (no bare white strip floating in the beige).
    <div
      className="flex flex-col h-full min-h-0 bg-white border overflow-hidden"
      style={{ borderColor: '#e7e0d0', borderRadius: 'var(--r-xl, 16px)', boxShadow: 'var(--sh-1, 0 2px 12px rgba(8,17,31,0.04))' }}
    >
      {/* ── Top bar: identity (title + source chip + live sync line) on the
             left; a bordered OVERLAY TOGGLE GROUP (Ask Vee / Details — these
             switch panels, they don't "do" things), a divider, then the real
             actions (Export / Generate) on the right. ── */}
      <div className="flex items-center gap-3 px-4 h-[54px] border-b bg-white shrink-0" style={{ borderColor: '#e7e0d0' }}>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 min-w-0">
            <span className="text-[13.5px] font-bold text-[#08111f] truncate" style={{ letterSpacing: '0.1px' }}>
              {activeCompany?.name || 'Select a company to start'}
            </span>
            {chip && (
              <span
                className="shrink-0 inline-flex items-center gap-1 h-[20px] px-2 text-[9.5px] font-semibold rounded-full"
                style={{ color: chip.color, background: chip.bg }}
              >
                <Icon name={chip.icon} size={11} />
                {chip.label}
              </span>
            )}
          </div>
          <div className="flex items-center gap-1.5 text-[10px] leading-tight truncate mt-[1px]" style={{ color: '#9a9080' }}>
            <span className="font-semibold uppercase tracking-[0.5px] text-[9px]">Minutes Studio</span>
            <span aria-hidden="true">·</span>
            {session ? (
              <span className="inline-flex items-center gap-1.5">
                <span className="w-[7px] h-[7px] rounded-full shrink-0" style={{ background: dirty ? '#b0893a' : '#3f8f5f' }} aria-hidden="true" />
                {dirty ? 'Unsaved edits — sync on click-away' : `Synced with Vee · v${session.rev}`}
                {docSource === 'user-edited' && lastAiSnapshotRef.current && (
                  <button
                    type="button"
                    onClick={discardMyEdits}
                    title="Restore Vee’s last version — throws away every hand-edit"
                    className="underline decoration-dotted underline-offset-2 hover:decoration-solid"
                    style={{ color: '#145f5f' }}
                  >
                    Discard my edits
                  </button>
                )}
              </span>
            ) : hydrated ? (
              <span>Chat or Generate starts the shared draft</span>
            ) : (
              // Same reason as the canvas skeleton: don't announce "no draft yet"
              // before the server has said whether there is one.
              <span>Opening the working draft…</span>
            )}
          </div>
        </div>

        <div className="flex items-center gap-2 shrink-0">
          {/* Overlay toggle group — one segmented surface, active = teal */}
          <div className="flex items-center gap-0.5 rounded-xl border p-0.5" style={{ borderColor: '#e2d9c8', background: '#faf8f2' }}>
            {[
              ['dock', 'Ask Vee', <FiMessageSquare size={13} key="i" />],
              ['inspector', 'Details', <FiSliders size={13} key="i" />],
            ].map(([key, label, icon]) => (
              <button
                key={key}
                type="button"
                aria-pressed={overlay === key}
                onClick={() => setOverlay((o) => (o === key ? null : key))}
                className="inline-flex items-center gap-1.5 h-8 px-3 rounded-[10px] text-[12px] font-semibold transition-colors active:scale-95"
                style={overlay === key
                  ? { background: '#145f5f', color: '#fff', boxShadow: 'var(--sh-1, 0 2px 12px rgba(8,17,31,0.04))' }
                  : { color: '#5a5145' }}
              >
                {icon} {label}
              </button>
            ))}
          </div>

          <div className="w-px h-6" style={{ background: '#e7e0d0' }} aria-hidden="true" />

          <ExportMenu size="sm" variant="outline" items={exportItems} disabled={!hasContent} />
          {/* Deliberately SECONDARY: Vee is the drafting path; this is the
              direct form→engine run (no conversation) for power users. */}
          <Button
            variant="outline"
            size="sm"
            leftIcon={<FiZap size={13} />}
            onClick={handleGenerate}
            loading={generating}
            disabled={generating || !activeCompany}
            title="Direct engine run from the form — Vee drafts the same thing in chat"
          >
            {hasDoc ? 'Regenerate' : 'Generate'}
          </Button>
        </div>
      </div>

      {/* ── Conflict banner ── */}
      {conflict && (
        <div className="flex items-center gap-3 px-4 py-2 border-b flex-wrap shrink-0" style={{ background: '#fdf4e7', borderColor: '#f0e3c5' }}>
          <FiAlertTriangle size={14} className="shrink-0" style={{ color: '#8a6a1f' }} />
          <span className="text-[12px]" style={{ color: '#5a5145' }}>Vee updated this draft while you were editing. Which version should win?</span>
          <span className="ml-auto flex items-center gap-2">
            <Button size="xs" variant="ghost" leftIcon={<FiRefreshCw size={12} />} onClick={() => { takeTheirs(); lastRenderedRev.current = 0; }}>
              Take Vee’s version
            </Button>
            <Button size="xs" variant="ghost" onClick={() => {
              const html = currentHtml();
              keepMine({ docHtml: html, docPlainText: htmlToPlainText(html), docSource: 'user-edited' });
            }}>
              Keep mine
            </Button>
            {/* The smart default: neither version is discarded. */}
            <Button size="xs" leftIcon={<Icon name="ai" size={12} />} onClick={letVeeMerge} title="Keep your edits and have Vee re-apply its update around them">
              Let Vee merge both
            </Button>
          </span>
        </div>
      )}

      {/* ── Document canvas (full width) + overlays ── */}
      <div className="flex-1 min-h-0 relative">
        {/* When an overlay is open the document re-centers in the space left
            of it (padding, not reserved layout — closing snaps back to full). */}
        <div
          className="absolute inset-0 overflow-y-auto cs-content-scroll"
          style={{
            background: '#f3efe5',
            paddingRight: narrow || !overlay ? 0 : overlayWidth + 16,
            transition: resizing.current ? 'none' : 'padding-right var(--dur-base, 200ms) ease',
          }}
        >
          <div className="max-w-[760px] mx-auto px-4 py-6">
            {hasContent ? (
              <div
                className="bg-white overflow-hidden border"
                style={{ borderColor: 'rgba(226,217,200,0.8)', borderRadius: 'var(--r-xl, 16px)', boxShadow: 'var(--sh-panel, 0 10px 30px -10px rgba(122,112,96,0.08))' }}
                onMouseUp={handleCanvasMouseUp}
              >
                <style>{DOC_CSS_SCOPED}</style>
                <div
                  ref={bodyRef}
                  className="doc-preview"
                  contentEditable
                  suppressContentEditableWarning
                  onFocus={() => { editingRef.current = true; }}
                  onInput={() => setDirty(true)}
                  onBlur={commitEdits}
                  style={{ fontFamily: "'Times New Roman', Times, serif", fontSize: '11.5pt', lineHeight: 1.7, color: '#000', padding: '42px 46px', outline: 'none', minHeight: '420px' }}
                />
              </div>
            ) : !hydrated ? (
              // The server hasn't answered for this company yet, and nothing was
              // cached locally. "Start the working draft" is a definitive claim
              // that there is no draft — don't make it while we're still asking,
              // or an existing document reads as missing for a round-trip.
              <div className="border border-dashed bg-white/60 px-8 py-12" style={{ borderColor: '#e6ddc9', borderRadius: 'var(--r-xl, 16px)' }} aria-busy="true">
                <div className="sr-only">Opening the working draft…</div>
                <div className="max-w-[430px] mx-auto space-y-3 animate-pulse" aria-hidden="true">
                  <div className="h-3 rounded mx-auto w-2/3" style={{ background: '#ece5d5' }} />
                  <div className="h-2 rounded mx-auto w-1/3" style={{ background: '#f1ebdd' }} />
                  <div className="h-px my-5" style={{ background: '#e6ddc9' }} />
                  {[1, 0.95, 0.85, 0.9, 0.6].map((w, i) => (
                    <div key={i} className="h-2 rounded" style={{ background: '#f1ebdd', width: `${w * 100}%` }} />
                  ))}
                </div>
              </div>
            ) : (
              <div className="border border-dashed bg-white/60 px-8 py-12 text-center" style={{ borderColor: '#d8cfba', borderRadius: 'var(--r-xl, 16px)' }}>
                <div className="text-[14px] font-semibold text-[#08111f] mb-1">Start the working draft</div>
                <div className="text-[12px] max-w-[430px] mx-auto leading-relaxed" style={{ color: '#7a7060' }}>
                  Tell Vee about the meeting (<b>Ask Vee</b>, top-right) and it drafts the minutes here —
                  or fill the form under <b>Details</b> first. Whatever is drafted, here or on the
                  Vee page, lands in this document.
                </div>
                <div className="mt-5 text-left opacity-60 pointer-events-none select-none bg-white border overflow-hidden" style={{ borderColor: '#eee7d8', borderRadius: 'var(--r-lg, 12px)' }}>
                  <style>{DOC_CSS_SCOPED}</style>
                  <div
                    className="doc-preview"
                    style={{ fontFamily: "'Times New Roman', Times, serif", fontSize: '10pt', lineHeight: 1.5, padding: '24px 28px', maxHeight: '320px', overflow: 'hidden' }}
                    dangerouslySetInnerHTML={{ __html: sanitizeDocHtml(getMeetingMinutesPreview(buildPayload(), companyContext)) }}
                  />
                </div>
              </div>
            )}
            <div className="text-[10.5px] text-center mt-3" style={{ color: '#9a9080' }}>
              Click anywhere in the document to edit — Vee sees your edits on its next change.
            </div>
          </div>
        </div>

        {/* Floating "Ask Vee about this" over the current document selection */}
        {selection && (
          <button
            type="button"
            onMouseDown={(e) => e.preventDefault()}
            onClick={quoteIntoDock}
            className="fixed z-[70] inline-flex items-center gap-1.5 px-3 h-8 rounded-full text-[11.5px] font-semibold text-white shadow-lg transition-transform active:scale-95"
            style={{ background: '#145f5f', left: Math.max(12, selection.x - 70), top: Math.max(12, selection.y - 40) }}
          >
            <FiMessageSquare size={12} /> Ask Vee about this
          </button>
        )}

        {/* Floating chat dock */}
        {overlay === 'dock' && (
          <div
            className={`absolute ${overlayFrame.className} z-30 ${resizing.current ? '' : 'animate-in slide-in-from-right duration-200'}`}
            style={{ ...overlayFrame.style, boxShadow: OVERLAY_SHADOW, border: '1px solid #e2d9c8', background: '#fff' }}
          >
            {/* Resize handle — same design as the AI chat's artifact panel:
                w-3 hit area straddling the edge, 3px pill that turns teal. */}
            {!narrow && (
              <div
                onMouseDown={startResize('dock')}
                className="absolute -left-1.5 top-0 bottom-0 w-3 cursor-col-resize flex items-center justify-center z-20 group/rz"
                aria-hidden="true"
                title="Drag to resize"
              >
                <div className="w-[3px] h-14 rounded-full bg-[#ddd6c6] group-hover/rz:bg-[#145f5f] transition-colors" />
              </div>
            )}
            <div className="absolute inset-0 overflow-hidden" style={{ borderRadius: 'inherit' }}>
              <CopilotDock
                getPageContext={bridge.getPageContext}
                onClientActions={bridge.handleClientActions}
                onArtifact={handleDockArtifact}
                onClose={() => setOverlay(null)}
                historyDraftKind="minutes"
                openSessionId={openChatSessionId}
                quote={quotedSelection}
                onClearQuote={() => setQuotedSelection(null)}
                autoSend={autoSend}
                onAutoSent={() => setAutoSend(null)}
              />
            </div>
          </div>
        )}

        {/* Floating inspector — handle lives on the NON-scrolling frame so it
            never scrolls away; content scrolls in the inner pane. */}
        {overlay === 'inspector' && (
          <div
            className={`absolute ${overlayFrame.className} z-30 border ${resizing.current ? '' : 'animate-in slide-in-from-right duration-200'}`}
            style={{ ...overlayFrame.style, boxShadow: OVERLAY_SHADOW, borderColor: '#e2d9c8', background: '#faf8f2' }}
          >
            {!narrow && (
              <div
                onMouseDown={startResize('inspector')}
                className="absolute -left-1.5 top-0 bottom-0 w-3 cursor-col-resize flex items-center justify-center z-20 group/rz"
                aria-hidden="true"
                title="Drag to resize"
              >
                <div className="w-[3px] h-14 rounded-full bg-[#ddd6c6] group-hover/rz:bg-[#145f5f] transition-colors" />
              </div>
            )}
            <div className="absolute inset-0 flex flex-col" style={{ borderRadius: 'inherit' }}>
              {/* Header — same chrome as the dock's (h, padding, type scale). */}
              <div className="flex items-center gap-2 px-3 py-2 border-b shrink-0" style={{ borderColor: '#e7e0d0', background: '#faf8f2' }}>
                <FiSliders size={13} style={{ color: '#145f5f' }} />
                <span className="text-[12px] font-bold text-[#08111f]">Meeting details</span>
                <button type="button" aria-label="Close details" className="ml-auto w-6 h-6 rounded-md flex items-center justify-center hover:bg-[#f0ece2]" style={{ color: '#9a9080' }} onClick={() => setOverlay(null)}>
                  <FiX size={14} />
                </button>
              </div>

              <div className="flex-1 min-h-0 overflow-y-auto cs-content-scroll p-3 space-y-2.5">
                {/* Hand the blanks to Vee. Deliberately NOT "Vee decides": it
                    fills only what the company record or this conversation
                    supports and asks about the rest — the same grounding rule
                    that governs every automatic fill. */}
                {missingFields.length > 0 && (
                  <div className="rounded-xl border p-3" style={{ borderColor: '#e2d9c8', background: '#fff' }}>
                    <div className="flex items-start gap-2">
                      <Icon name="ai" size={14} style={{ color: '#145f5f' }} />
                      <div className="min-w-0">
                        <div className="text-[11.5px] font-semibold text-[#08111f]">
                          {missingFields.length} detail{missingFields.length === 1 ? '' : 's'} still blank
                        </div>
                        <div className="text-[10.5px] leading-relaxed mt-[2px]" style={{ color: '#7a7060' }}>
                          Vee can fill what your company record already answers — and will ask you about the rest rather than guess.
                        </div>
                      </div>
                    </div>
                    <Button size="sm" variant="outline" className="w-full mt-2.5" leftIcon={<Icon name="ai" size={13} />} onClick={askVeeToFillGaps}>
                      Let Vee fill the gaps
                    </Button>
                  </div>
                )}
                <StudioConfigSections flashKeys={bridge.flashKeys} />

                {/* Statutory page numbering — styled as one more Section card so
                    the rail reads as a single system. */}
                <div className="rounded-xl border bg-white overflow-hidden" style={{ borderColor: '#e2d9c8' }}>
                  <div className="px-3 py-2 text-[11px] font-bold uppercase tracking-[0.5px] text-[#08111f]">Page numbering</div>
                  <div className="px-3 pb-3 space-y-2">
                    <PageContinuityBanner
                      companyCin={companyContext?.cin || activeCompany?.cin || ''}
                      meetingType={config.meetingType}
                      meetingDate={config.date}
                      refreshTick={ledgerRefreshTick}
                      onLedger={setLedgerInfo}
                    />
                    <StartPageOverride
                      suggestedStartPage={ledgerInfo.suggestedStartPage}
                      value={startPageOverride}
                      onChange={setStartPageOverride}
                      lockedByContinuity={ledgerInfo.hasHistory}
                    />
                  </div>
                </div>

                {/* No Generate here — the form feeds Vee; drafting is Vee's job
                    (top-bar Generate stays for the direct path). */}
                <div className="rounded-xl border p-3" style={{ borderColor: '#cfe0dd', background: 'rgba(20,95,95,0.05)' }}>
                  <div className="text-[11.5px] leading-relaxed" style={{ color: '#145f5f' }}>
                    All set? Hand it to Vee — every detail on this form rides along automatically.
                  </div>
                  <Button size="sm" className="w-full mt-2" leftIcon={<FiMessageSquare size={13} />} onClick={() => setOverlay('dock')}>
                    Ask Vee to draft
                  </Button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
